import { DisplayWhenHasModules } from 'components.layouts/five-up/five-up-utils.js';
import { ModuleArrLoad } from 'layout/module-load/module-load-arr.js';
import { ModulesLazyLoadWrapper } from 'layout/module-load/modules-lazy-load-wrapper.js';
import { getModulesMetaData } from 'layout/module-load/modules-meta-data.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext } from 'react';
import { ILayoutConfigBase, IPrimarySlotPropsMap } from 'redux-state/layout/types.layout.js';
import { ModulePath } from 'utilities/modulePath/modulePath.js';
import { MapLargeLeftSlotWrapper } from './map-large-left-slot-wrapper.js';
import { MapLeftSlotAdditional } from './map-left-slot-additional.js';
import { MapLeftSlotDynamic } from './map-left-slot-dynamic.js';
import { MapWrapper } from './map-wrapper-large.js';

export type MapLeftSlotProps = {
  filteredPrimaryConfig: IPrimarySlotPropsMap;
  children: JSX.Element | null;
  mobileView: ILayoutConfigBase['mobileView'];
};

/**
 * Renders map layout with slots over the top of map canvas
 * MapSlot callback is passed to map which is used to update map slot on icon select
 */
export const MapLeftSlot = ({
  /** searchBarOffset increases when searchbar is displaying */
  filteredPrimaryConfig,
  children,
  mobileView,
}: MapLeftSlotProps) => {
  const columnLayout = useContext(ColumnLayoutContext);
  const isLarge = columnLayout !== '1-column';

  if (isLarge) {
    return (
      <>
        <MapLargeLeftSlotWrapper filteredPrimaryConfig={filteredPrimaryConfig}>
          <>
            <MapLeftSlotDynamic classes="u-mB-responsive" name="map-lg" />
            <MapLeftSlotAdditional
              filteredPrimaryConfig={filteredPrimaryConfig}
              classes="u-mB-responsive"
              name="map-lg"
            />
          </>
        </MapLargeLeftSlotWrapper>
        <MapWrapper filteredPrimaryConfig={filteredPrimaryConfig}>{children}</MapWrapper>
      </>
    );
  }

  /** Used on homepage */
  if (mobileView === 'primary-first') {
    const { leftMajor, leftMinor } = filteredPrimaryConfig;
    return (
      <div data-module-name="map-forecast-small-mobile" className="Grid-cell">
        <DisplayWhenHasModules section={leftMajor}>
          <ModulesLazyLoadWrapper
            section={getModulesMetaData(`map-small-leftMajor`, leftMajor.modules)}
            className="u-mB-responsive"
          >
            <ModuleArrLoad modules={leftMajor.modules} modulePath={ModulePath.PRIMARY_LEFT_MAJOR} />
          </ModulesLazyLoadWrapper>
        </DisplayWhenHasModules>
        {children}
        <MapLeftSlotDynamic classes="u-mB-responsive u-mT-responsive" name="map-small" />
        <DisplayWhenHasModules section={leftMinor}>
          <ModulesLazyLoadWrapper
            section={getModulesMetaData(`map-small-leftMinor`, leftMinor.modules)}
            className="u-mB-responsive u-mT-responsive"
          >
            <ModuleArrLoad modules={leftMinor.modules} modulePath={ModulePath.PRIMARY_LEFT_MINOR} />
          </ModulesLazyLoadWrapper>
        </DisplayWhenHasModules>
      </div>
    );
  }

  return (
    <div data-module-name="map-forecast-small" className="Grid-cell">
      {children}
      <MapLeftSlotDynamic classes="u-mB-responsive u-mT-responsive" name="map-small" />
      <MapLeftSlotAdditional
        filteredPrimaryConfig={filteredPrimaryConfig}
        classes="u-mB-responsive u-mT-responsive"
        name="map-small"
      />
    </div>
  );
};
